<template>
  <modal-dialog
    ref="dialog"
    :visible="dialog"
    persistent
    scrollable
    max-width="600px"
    card-class="pa-0 pb-5 mb-5"
    content-class="dialog-height-60"
    container-class="pa-0"
    :title="title"
  >
    <!-- Tab Allocations Dialog -->
    <guests-allocations-dialog
      :visible="guestsDialog"
      :party-size="guestsPartySize"
      :mode="mode"
      @close="guestsDialog = false"
    />

    <!-- New Table Combination Dialog -->
    <table-combination-dialog
      :visible="addTableCombinationDialog"
      :mode="mode"
      @close="closeTableCombinationDialog"
    />

    <!-- Data Table -->
    <validation-observer ref="observer">
      <v-data-table
        :item-class="dtClassForItem"
        mobile-breakpoint="0"
        :headers="headers"
        :items="items"
        disable-pagination
        hide-default-footer
        :no-data-text="$t('label.no_allocations')"
        @click:row="editAllocations"
      >
        <template #item.partySize="props">
          <span
            :class="[{'error--text' : props.item.isError}]"
          >
            {{ $t('label.x_guests', [props.item.partySize]) }}
          </span>
        </template>
        <template #item.allocationsCount="props">
          <span
            :class="[{'error--text' : props.item.isError} ]"
          >
            {{ $t('label.x_allocations', [props.item.allocationsCount]) }}
          </span>
        </template>
      </v-data-table>
    </validation-observer>

    <!-- Actions -->
    <template #actions>
      <v-btn
        color="primary"
        @click="add"
      >
        {{ $t('button.add_new_table_combination') }}
      </v-btn>
      <v-spacer />
      <v-btn
        color="primary"
        text
        @click="close"
      >
        {{ $t('button.done') }}
      </v-btn>
    </template>
  </modal-dialog>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import DialogClosable from '@/mixins/dialog-closable';
import GuestsAllocationsDialog from '@/components/views/settings/GuestsAllocationsDialog.vue';
import TableCombinationDialog from '@/components/views/settings/TableCombinationDialog.vue';
import Tab from '@/model/Tab';
import Allocation from '@/model/Allocation';

type Item = { partySize: number, allocationsCount: number, isError: boolean };

export enum AllocationsDialogMode {
  AllowedAllocations,
  DisallowedAllocations,
}

export default Vue.extend({
  name: 'TabAllocationsDialog',
  components: { GuestsAllocationsDialog, TableCombinationDialog },
  mixins: [DialogClosable],
  props: {
    mode: { type: Number as PropType<AllocationsDialogMode>, required: true },
  },
  data() {
    return {
      headers: [
        {
          text: this.$i18n.t('label.party_size'),
          value: 'partySize',
          align: 'start',
          sortable: false,
        },
        {
          text: this.$i18n.t('label.allocations'),
          value: 'allocationsCount',
          align: 'end',
          sortable: false,
        },
      ],
      addTableCombinationDialog: false,
      guestsDialog: false,
      // maxPartySize: 0,
      guestsPartySize: 0,
    };
  },
  computed: {
    title(): string {
      switch (this.mode) {
        case AllocationsDialogMode.AllowedAllocations: return this.$i18n.tc('button.preferred_allocations');
        case AllocationsDialogMode.DisallowedAllocations: return this.$i18n.tc('button.disallowed_allocations');
        default: return 'N/A';
      }
    },
    allocationsByPartySize(): Map<number, Allocation[]> {
      switch (this.mode) {
        case AllocationsDialogMode.AllowedAllocations:
          return this.$tstore.getters.allowedAllocationsByPartySize;
        case AllocationsDialogMode.DisallowedAllocations:
          return this.$tstore.getters.disallowedAllocationsByPartySize;
        default: return new Map<number, Allocation[]>();
      }
    },
    items(): Item[] {
      const items: Item[] = [];
      for (let partySize = 1; partySize <= this.maxPartySize; partySize += 1) {
        const allocations = this.allocationsByPartySize.get(partySize) ?? [];
        const isError = this.isErrorWithinAllocations(allocations);
        items.push({ partySize, isError, allocationsCount: allocations.length });
      }
      return items;
    },
    maxPartySize(): number {
      return Math.max(...this.allocationsByPartySize.keys(), 0);
    },
  },
  watch: {
  },
  methods: {
    add() {
      this.addTableCombinationDialog = true;
    },
    close() {
      this.$emit('close');
    },
    closeTableCombinationDialog() {
      this.addTableCombinationDialog = false;
    },
    editAllocations(item: Item) {
      if (item.allocationsCount === 0) { return; }
      this.guestsDialog = true;
      this.guestsPartySize = item.partySize;
    },
    isErrorWithinAllocations(allocations: Allocation[]): boolean {
      const map = new Set<string>();
      return !allocations.every((o) => (
        map.has(o.tableIdsString) || o.isError ? false : map.add(o.tableIdsString)), true);
    },
    dtClassForItem(item: Item) {
      if (item.allocationsCount === 0) {
        return 'disabled-table-row grey--text';
      }
      return 'clickable-table';
    },
  },
});
</script>

<style scoped>

  .disabled-table-row {
    cursor: default!important;
  }

</style>
